/** @prettier */
import debounce from 'just-debounce-it';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';

import { searchClear, searchRequest, setSearchQuery } from '../../../modules/search/actions';

import { getClientString } from '../../../utils';

import IconClose from '../../../../../images/icons/misc/icon-close.svg';

import styles from './SearchBar.module.scss';

const SearchBar = ({
  onQuery,
  onQueryClear,
  setQuery,
  showLoader = true,
  queryValue,
  loadingResults,
  clientName,
  clientSite,
  onInputFocusChange = () => {},
}) => {
  const { t } = useImprovedTranslation();
  const debouncedQuery = useMemo(() => debounce(onQuery, 300), []);
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = ({ target: { value: newValue } }) => {
    setQuery(newValue);
  };

  const handleClose = () => {
    setQuery('');
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  useEffect(() => {
    if (queryValue === '') {
      debouncedQuery.cancel();
      onQueryClear();
      onInputFocusChange(false);
    }

    if (queryValue.length > 2) {
      debouncedQuery(queryValue);
      onInputFocusChange(true);
    }
  }, [queryValue, debouncedQuery]);

  const clientString = getClientString(clientName, clientSite);

  return (
    <div className={styles.main}>
      <input
        type="text"
        data-hook="search-top"
        className={styles.input}
        name="search-top"
        autoComplete="off"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={queryValue}
      />
      {!queryValue && !isFocused && (
        <div className={styles.placeholder}>
          <span>{t('Search')}</span>
          {'  '}
          <span className={styles.placeholderBrand}>{clientString}</span>
        </div>
      )}
      {loadingResults && showLoader && (
        <svg className={styles.loader} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="45" />
        </svg>
      )}
      {Boolean(queryValue.length) && (
        <i role="button" className={styles.closeButton} onClick={handleClose}>
          <IconClose />
        </i>
      )}
    </div>
  );
};

SearchBar.propTypes = {
  onQuery: PropTypes.func.isRequired,
  onQueryClear: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
  clientName: PropTypes.string.isRequired,
  clientSite: PropTypes.string.isRequired,
  queryValue: PropTypes.string.isRequired,
  loadingResults: PropTypes.bool.isRequired,
  showLoader: PropTypes.bool,
  onInputFocusChange: PropTypes.func,
};

const mapDispatchToProps = {
  onQuery: searchRequest,
  onQueryClear: searchClear,
  setQuery: setSearchQuery,
};

function mapStateToProps(state) {
  return {
    loadingResults: state.getIn(['local', 'search', 'loading']),
    queryValue: state.getIn(['local', 'search', 'value']),
    clientName: state.getIn(['application', 'clientName']),
    clientSite: state.getIn(['application', 'site']),
  };
}

export const Component = SearchBar;

export default connect(mapStateToProps, mapDispatchToProps)(SearchBar);
